import { Input } from "@components/Input";
import { HTable } from "@components/Table";
import { FormControl, Grid, SvgIcon, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { COLORS } from "@shared/constants/colors";
import { LabelControl, Title } from "@shared/styledComponents";
import { Column, PaginatedResult } from "types/common";
import { useEffect, useState } from "react";
import { storeOpenSnackbar, storeSetGlobalLoading } from "@store/globalReducer";
import { apiClient } from "@services/baseAxios";
import { ApiConfig } from "@config/index";
import { errorMessages } from "@shared/constants/messages";
import { useDispatch } from "react-redux";
import { rowCustomHandlerType } from "@components/Table/constant";
import { ReactComponent as GrayEditIcon } from '@assets/icons/icon-edit-gray.svg';
import { ReactComponent as GrayBucketIcon } from '@assets/icons/icon-bucket-gray.svg';
import { HButton } from "@components/Button";
import AddIcon from '@mui/icons-material/Add';
import { ProductForm } from "./productForm";
import { HDialog } from "@components/Dialog";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const Wrapper = styled('div')(() => ({
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.GhostWhite,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column'
}));

const ToolbarWrapper = styled('div')(() => ({
    padding: "16px 0px"
}));

const columns: Column[] = [
    {
        id: 'STT',
        label: 'STT',
        align: 'left',
        minWidth: 30,
        transform: (data: any) => (data.index + 1)
    },
    {
        id: 'Name',
        label: 'Tên',
        align: 'left',
        minWidth: 100,
    },
    {
        id: 'Code',
        label: 'Mã',
        align: 'left',
        minWidth: 40,
    },
    {
        id: 'ProductTypeName',
        label: 'Loại sản phẩm',
        minWidth: 200,
        align: 'left',
    },
    {
        id: 'Actions',
        align: 'center',
        label: 'Tác vụ',
        minWidth: 80,
        transform: (value: any) => (
            <div className="flex justify-center items-center">
                <Tooltip title="Sửa" placement="left">
                    <div onClick={() => value.onCustomHandler(rowCustomHandlerType.EDIT)} className='flex justify-center items-center cursor-pointer'>
                        <SvgIcon sx={{ width: 16, height: 16, color: COLORS.Green }} component={GrayEditIcon} inheritViewBox />
                    </div>
                </Tooltip>
                <Tooltip className="ml-2" title="Xóa" placement="top">
                    <div onClick={() => value.onCustomHandler(rowCustomHandlerType.DELETE)} className='flex justify-center items-center cursor-pointer'>
                        <SvgIcon sx={{ width: 16, height: 16, color: COLORS.Green }} component={GrayBucketIcon} inheritViewBox />
                    </div>
                </Tooltip>
            </div>
        )
    },
];

export const ProductAdministration = () => {
    const dispatch = useDispatch();

    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [tableLoading, setTableLoading] = useState(false);
    const [isCreateProduct, setIsCreateProduct] = useState(false);
    const [productFormTitle, setLocationFormTitle] = useState("Sửa sản phẩm");
    const [selectedRow, setSelectedRow] = useState({} as any);
    const [isOpenProductForm, setIsOpenProductForm] = useState(false);
    const [isOpenConfirmDeleteLocationDialog, setIsOpenConfirmDeleteLocationDialog] = useState(false);
    const [paginatedData, setPaginatedData] = useState<PaginatedResult>({
        ListOut: [],
        TotalCount: 0,
        PageStart: 1,
        PageEnd: 1,
        TotalPage: 0
    } as PaginatedResult);

    const onRowCustomHandler = (row: any, handlerType: any) => {
        setSelectedRow(row);
        if (handlerType === rowCustomHandlerType.EDIT) {
            setLocationFormTitle("Sửa sản phẩm");
            setIsCreateProduct(false);
            setIsOpenProductForm(true);
        }
        else if (handlerType === rowCustomHandlerType.DELETE) {
            setIsOpenConfirmDeleteLocationDialog(true);
        }
    }

    const closeConfirmDeleteDialog = () => {
        setIsOpenConfirmDeleteLocationDialog(false);
    }

    const deleteLocation = async () => {
        try {
            dispatch(storeSetGlobalLoading(true));
            var { AppCode } = await apiClient.delete(`${ApiConfig.PRODUCT}/${selectedRow.Id}`);
            dispatch(storeSetGlobalLoading(false));
            if (AppCode === 200) {
                closeConfirmDeleteDialog();
                dispatch(storeOpenSnackbar({ open: true, message: "Xóa sản phẩm thành công", type: "success" }));
                getProductPaging();
            }
            else if (AppCode === 409) {
                dispatch(storeOpenSnackbar({ open: true, message: "Dữ liệu về sản phẩm đã được phát sinh ở nhiều nơi, không thể xóa sản phẩm này", type: "error" }));
            }
            else if (AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: "Xóa sản phẩm thất bại", type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: "Xóa người dùng thất bại", type: "error" }));
            }
        } catch (error: any) {
            dispatch(storeSetGlobalLoading(false));
            if (error?.response?.data?.AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: error?.response?.data?.Message, type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        }
    }

    const getObjectFilter = () => {
        return {
            Keyword: keyword,
            PageIndex: page + 1,
            PageSize: rowsPerPage,
        };
    }

    const getProductPaging = async () => {
        try {
            setTableLoading(true);
            var { AppCode, Data } = await apiClient.post(`${ApiConfig.PRODUCT}/paging`, getObjectFilter());
            setTableLoading(false);
            if (AppCode === 200) {
                setPaginatedData((prevState: PaginatedResult) => ({
                    ...prevState,
                    ...Data
                }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        } catch (error: any) {
            dispatch(storeSetGlobalLoading(false));
            if (error?.response?.data?.AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: error?.response?.data?.Message, type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        }
    }

    const handleSubmitSuccess = () => {
        handleCloseForm();
        var msg = isCreateProduct ? "Tạo sản phẩm thành công" : "Sửa sản phẩm thành công";
        dispatch(storeOpenSnackbar({ open: true, message: msg, type: "success" }));
        getProductPaging();
    }

    const handleCloseForm = () => {
        setIsOpenProductForm(false);
        setSelectedRow({})
    }

    const handleChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    useEffect(() => {
        getProductPaging();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, keyword]);

    return (
        <Wrapper>
            <div className="w-full flex justify-between">
                <Title>Danh sách sản phẩm</Title>
                <HButton startIcon={<AddIcon />} onClick={() => {
                    setLocationFormTitle("Tạo sản phẩm")
                    setIsCreateProduct(true);
                    setIsOpenProductForm(true);
                }}>Tạo sản phẩm</HButton>
            </div>
            <ToolbarWrapper>
                <Grid container>
                    <Grid sx={{ pt: 0, pl: 0 }} item xs={6}>
                        <FormControl sx={{ width: '100%' }} variant="standard">
                            <LabelControl>Tên hoặc mã</LabelControl>
                            <Input onChange={handleChangeKeyword} value={keyword} sx={{ width: '100%', marginTop: '4px' }} placeholder="Tìm kiếm theo tên hoặc mã" />
                        </FormControl>
                    </Grid>
                </Grid>
            </ToolbarWrapper>
            <HTable
                columns={columns}
                pagingInfo={{
                    rowsPerPage,
                    page,
                    totalCount: paginatedData.TotalCount,
                    handleChangePage,
                    handleChangeRowsPerPage
                }}
                loading={tableLoading}
                listData={paginatedData.ListOut}
                onRowCustomHandler={onRowCustomHandler}
            ></HTable>
            {
                isOpenProductForm &&
                <ProductForm
                    isOpen={isOpenProductForm}
                    isCreateNew={isCreateProduct}
                    title={productFormTitle}
                    productData={selectedRow}
                    handleOnClose={handleCloseForm}
                    handleOnConfirm={handleSubmitSuccess}
                >
                </ProductForm>
            }
            {/* Popup confirm xóa sản phẩm */}
            <HDialog
                PaperProps={{
                    sx: {
                        width: "450px",
                    }
                }}
                title="Xóa sản phẩm"
                open={isOpenConfirmDeleteLocationDialog}
                handleClose={closeConfirmDeleteDialog}
                dialogFooter={
                    <>
                        <HButton startIcon={<CloseOutlinedIcon />} btnType="secondary" onClick={closeConfirmDeleteDialog}>Hủy</HButton>
                        <HButton startIcon={<DeleteOutlineIcon />} onClick={deleteLocation}>Xóa</HButton>
                    </>
                }
            >
                Bạn chắc chắn muốn sản phẩm này chứ?
            </HDialog>
        </Wrapper>
    )
}